import axios from 'axios'
import { Logger, LogAttributes, SeverityNumber, logs } from '@opentelemetry/api-logs'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'

const DEFAULT_SERVICE_NAME = 'astro-app-web-admin'
const DEFAULT_SERVICE_VERSION = 'unknown'
const DEFAULT_ENVIRONMENT = 'development'
const EXPORT_SUCCESS_CODE = 0
const reportedDiagnosticMessages = new Set<string>()
const attributeKey = {
  appAction: 'app.action',
  appDomain: 'app.domain',
  appPlatform: 'app.platform',
  deploymentEnvironment: 'deployment.environment',
  exceptionMessage: 'exception.message',
  exceptionStacktrace: 'exception.stacktrace',
  exceptionType: 'exception.type',
  httpMethod: 'http.method',
  httpStatusCode: 'http.status_code',
  httpUrl: 'http.url',
  serviceName: 'service.name',
  serviceVersion: 'service.version',
  sessionId: 'session.id',
  userId: 'user.id',
} as const

type OpenTelemetryAttributeValueType = string | number | boolean
type ExportParametersType = Parameters<OTLPLogExporter['export']>

export type LogOpenTelemetryApiErrorPayloadType = {
  action: string
  attributes?: Record<string, OpenTelemetryAttributeValueType | undefined>
  domain: string
  error: unknown
  message?: string
  sessionId?: string
  userId?: string | number
}

let logger: Logger | undefined

const reportDiagnosticError = (message: string) => {
  if (reportedDiagnosticMessages.has(message)) {
    return
  }

  reportedDiagnosticMessages.add(message)
  // eslint-disable-next-line no-console
  console.error(`[OpenTelemetry] ${message}`)
}

const addExporterDiagnostics = (exporter: OTLPLogExporter) => {
  const exportLogs = exporter.export.bind(exporter)

  return {
    export: (...[records, resultCallback]: ExportParametersType) => {
      exportLogs(records, (result) => {
        if (result.code !== EXPORT_SUCCESS_CODE) {
          reportDiagnosticError('Failed to export logs to collector')
        }

        resultCallback(result)
      })
    },
    forceFlush: () => exporter.forceFlush(),
    shutdown: () => exporter.shutdown(),
  }
}

const getConfig = () => ({
  authorization: process.env.REACT_APP_OTEL_AUTH_HEADER,
  enabled: process.env.REACT_APP_ENABLE_OTEL === 'true',
  endpoint: process.env.REACT_APP_OTEL_LOGS_ENDPOINT,
  environment: process.env.REACT_APP_ENV || DEFAULT_ENVIRONMENT,
  serviceName: process.env.REACT_APP_NAME || DEFAULT_SERVICE_NAME,
  serviceVersion: process.env.REACT_APP_VERSION || DEFAULT_SERVICE_VERSION,
})

const removeUndefinedAttributes = (
  attributes: Record<string, OpenTelemetryAttributeValueType | undefined>,
): LogAttributes =>
  Object.entries(attributes).reduce<LogAttributes>(
    (result, [key, value]) => (value === undefined ? result : { ...result, [key]: value }),
    {},
  )

const getSanitizedUrl = (url?: string, baseUrl?: string) => {
  if (!url) {
    return undefined
  }

  try {
    const parsedUrl = new URL(url, baseUrl || window.location.origin)
    parsedUrl.search = ''
    parsedUrl.hash = ''

    return parsedUrl.toString()
  } catch {
    return url.split(/[?#]/)[0]
  }
}

export const initOpenTelemetryLog = () => {
  if (logger) {
    return true
  }

  const config = getConfig()

  if (!config.enabled) {
    return false
  }

  if (!config.endpoint || !config.authorization) {
    reportDiagnosticError('Collector endpoint or authorization is missing')
    return false
  }

  try {
    const exporter = addExporterDiagnostics(
      new OTLPLogExporter({
        headers: {
          Authorization: config.authorization,
        },
        url: config.endpoint,
      }),
    )
    const provider = new LoggerProvider({
      processors: [new BatchLogRecordProcessor({ exporter })],
      resource: resourceFromAttributes({
        [attributeKey.appPlatform]: 'web',
        [attributeKey.deploymentEnvironment]: config.environment,
        [attributeKey.serviceName]: config.serviceName,
        [attributeKey.serviceVersion]: config.serviceVersion,
      }),
    })

    logs.setGlobalLoggerProvider(provider)
    logger = provider.getLogger(config.serviceName, config.serviceVersion)

    return true
  } catch {
    reportDiagnosticError('Failed to initialize log exporter')
    return false
  }
}

export const logOpenTelemetryApiError = ({
  action,
  attributes = {},
  domain,
  error,
  message,
  sessionId,
  userId,
}: LogOpenTelemetryApiErrorPayloadType) => {
  if (!logger && !initOpenTelemetryLog()) {
    return
  }

  const apiError = axios.isAxiosError(error) ? error : undefined
  const exception = error instanceof Error ? error : undefined
  const httpMethod = apiError?.config?.method?.toUpperCase()
  const httpUrl = getSanitizedUrl(apiError?.config?.url, apiError?.config?.baseURL)
  const statusCode = apiError?.response?.status

  logger?.emit({
    attributes: removeUndefinedAttributes({
      ...attributes,
      [attributeKey.appAction]: action,
      [attributeKey.appDomain]: domain,
      [attributeKey.exceptionMessage]: exception?.message,
      [attributeKey.exceptionStacktrace]: exception?.stack,
      [attributeKey.exceptionType]: exception?.name,
      [attributeKey.httpMethod]: httpMethod,
      [attributeKey.httpStatusCode]: statusCode,
      [attributeKey.httpUrl]: httpUrl,
      [attributeKey.sessionId]: sessionId,
      [attributeKey.userId]: userId,
    }),
    body: message || exception?.message || 'API request failed',
    eventName: 'api.error',
    severityNumber: SeverityNumber.ERROR,
    severityText: 'ERROR',
  })
}
